/**
 * Recovery for the "a new release was deployed while this tab was open" failure mode.
 *
 * Production builds use `outputHashing: all`, so every release renames every lazy chunk, and both
 * deploy pipelines wipe the site folder before extracting the new build. A tab still running the
 * previous build therefore asks for chunk filenames that no longer exist on the server. Without
 * this recovery the lazy `import()` rejects, LoggingErrorHandler logs and swallows it, and the view
 * stays broken until the user does a hard refresh.
 *
 * Reloading is only safe once the server can answer for the site root again. The deploy deletes the
 * whole website folder - index.html and Web.config included - before extracting, so for as long as
 * the extract runs IIS has no default document and none of our rewrite rules, and answers "/" with
 * its own "403 - Forbidden" page. A reload that lands inside that window navigates the browser off
 * the SPA onto that page, and from there none of our script is left running to try again: the user
 * is stranded until they refresh by hand. So every reload below first waits behind an overlay until
 * the server reports the new build is in place, and offers a button rather than navigating blind.
 */

/** Query parameter used to force a new HTTP cache key for the recovery reload. */
const CACHE_BUST_PARAM = "_build";

/** sessionStorage key holding the timestamp of the last recovery reload. */
const RELOAD_MARKER_KEY = "fm.staleBuildReloadedAt";

/** Reload at most once per window, so a genuinely broken build cannot loop the browser. */
const RELOAD_COOLDOWN_MS = 60_000;

/** Follow at most this many wrapper layers when sniffing an error. */
const MAX_UNWRAP_DEPTH = 5;

/**
 * The deploy writes server_variables.json in its last step, after the archive is extracted, so a
 * 200 here means the whole build is on disk - not just the part IIS has unpacked so far.
 */
const READY_PROBE_URL = "./server_variables.json";

/** Poll cadence while waiting out a deploy: starts here and backs off to the maximum. */
const READY_POLL_START_DELAY_MS = 1_000;
const READY_POLL_MAX_DELAY_MS = 5_000;

/** Give the deploy this long to put the site back before handing the decision to the user. */
const READY_POLL_TIMEOUT_MS = 90_000;

/** Element id of the "we are updating" cover, so a second call cannot stack two of them. */
const OVERLAY_ID = "fm_update_overlay";

/**
 * Messages browsers and bundlers produce when a lazy chunk cannot be fetched or parsed. The
 * MIME-type variant is what our IIS sites produced while a missing chunk was still being rewritten
 * to index.html; it is kept so tabs running an older build still recover.
 */
const STALE_BUILD_MESSAGES = [/loading chunk \S+ failed/i, /loading css chunk \S+ failed/i, /failed to fetch dynamically imported module/i, /error loading dynamically imported module/i, /importing a module script failed/i, /expected a javascript(-or-wasm)? module script/i];

/** True when the error means "the code this build asked for is no longer on the server". */
export function isStaleBuildError(error: unknown, depth = 0): boolean {
  if (error == null || depth > MAX_UNWRAP_DEPTH) {
    return false;
  }

  if (typeof error === "string") {
    return STALE_BUILD_MESSAGES.some((pattern) => pattern.test(error));
  }

  if (typeof error !== "object") {
    return false;
  }

  const candidate = error as { name?: unknown; message?: unknown; rejection?: unknown; reason?: unknown; originalError?: unknown };

  if (candidate.name === "ChunkLoadError") {
    return true;
  }

  const message = candidate.message;
  if (typeof message === "string" && STALE_BUILD_MESSAGES.some((pattern) => pattern.test(message))) {
    return true;
  }

  // Zone.js, the router and Angular's ErrorHandler each wrap the original rejection differently.
  return isStaleBuildError(candidate.rejection, depth + 1) || isStaleBuildError(candidate.reason, depth + 1) || isStaleBuildError(candidate.originalError, depth + 1);
}

/**
 * Drop the cached build and reload as soon as the server is serving again. Returns false when a
 * reload already ran recently, so callers can fall back to reporting the error normally instead of
 * fighting the browser.
 */
export function recoverFromStaleBuild(reason: string): boolean {
  if (!canReload()) {
    console.warn(`[Update] ${reason}, but a recovery reload already ran; leaving the page alone.`);
    return false;
  }

  markReloadAttempt();
  console.warn(`[Update] ${reason}; clearing the cached build and reloading.`);
  void dropCachedBuild().then(() => reloadWhenServerIsReady());
  return true;
}

/**
 * Pick up a release we already know about (service worker event or version poll). Nothing is broken
 * yet, so the cached build is left alone and we only have to wait out the deploy window.
 */
export function reloadForNewBuild(reason: string): void {
  console.warn(`[Update] ${reason}; reloading onto the new build.`);
  void reloadWhenServerIsReady();
}

/** Strip the cache-busting parameter after a successful boot so it cannot get bookmarked. */
export function clearCacheBustParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) {
    return;
  }

  url.searchParams.delete(CACHE_BUST_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

function canReload(): boolean {
  const lastAttempt = Number(readReloadMarker());
  if (!Number.isFinite(lastAttempt) || lastAttempt <= 0) {
    return true;
  }
  return Date.now() - lastAttempt > RELOAD_COOLDOWN_MS;
}

function readReloadMarker(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch {
    // Private-mode browsers can refuse sessionStorage; the cooldown is best-effort.
    return null;
  }
}

function markReloadAttempt(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, Date.now().toString());
  } catch {
    // See readReloadMarker.
  }
}

/**
 * On the Trips build a service worker serves the app shell, so its caches hold the same dead
 * chunk list as the browser. Unregistering and clearing them is what makes the reload land on the
 * new build instead of replaying the broken one.
 */
async function dropCachedBuild(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((key) => key.startsWith("ngsw:")).map((key) => caches.delete(key)));
    }
  } catch (error) {
    console.warn("[Update] Could not clear the cached build; reloading anyway.", error);
  }
}

/**
 * Cover the app and reload the moment the server has the new build. Usually the deploy is long
 * finished by the time we get here and the first probe answers, so this costs a flash of overlay;
 * when we caught the site mid-deploy it is what keeps the user inside the app instead of on the
 * IIS 403 page.
 */
async function reloadWhenServerIsReady(): Promise<void> {
  const overlay = showUpdatingOverlay();
  const deadline = Date.now() + READY_POLL_TIMEOUT_MS;
  let delay = READY_POLL_START_DELAY_MS;

  while (Date.now() < deadline) {
    if (await serverIsReady()) {
      reloadOnFreshBuild();
      return;
    }

    await wait(delay);
    delay = Math.min(delay * 1.5, READY_POLL_MAX_DELAY_MS);
  }

  // Whatever this is, it outlasted a deploy: the site is down, or the network is. Reloading now
  // would only trade this overlay for a server error page, so let the user decide.
  console.warn("[Update] The server did not come back in time; offering a manual reload.");
  overlay.offerManualReload();
}

async function serverIsReady(): Promise<boolean> {
  try {
    const response = await fetch(READY_PROBE_URL, { cache: "no-store" });
    return response.ok;
  } catch {
    // Offline, or the server refused the connection while the site was being replaced.
    return false;
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Full-screen cover, deliberately built with plain DOM calls and styled inline: it has to render
 * while the Angular app is in a broken state and while the only code we can count on is what the
 * browser has already parsed. Mirrors the #fm_loader boot screen in index.html so it reads as the
 * same application.
 */
function showUpdatingOverlay(): { offerManualReload: () => void } {
  document.getElementById(OVERLAY_ID)?.remove();

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");
  Object.assign(overlay.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    zIndex: "2147483647",
    backgroundColor: "#eee",
    color: "#676a6c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  });

  const panel = document.createElement("div");
  Object.assign(panel.style, { maxWidth: "360px", padding: "15px" });

  const logo = document.createElement("img");
  // Already painted by #fm_loader at boot, so this normally comes straight from the memory cache.
  // If we caught the deploy mid-extract the file is briefly gone; drop the broken image quietly.
  logo.src = "/assets/images/360_logo.png";
  logo.alt = "";
  Object.assign(logo.style, { width: "100%", maxWidth: "330px", padding: "15px" });
  logo.onerror = (): void => logo.remove();

  const message = document.createElement("p");
  message.textContent = "A new version is being installed. This page will reload automatically.....";

  const reload = document.createElement("button");
  reload.type = "button";
  reload.className = "btn btn-primary";
  reload.textContent = "Reload now";
  Object.assign(reload.style, { display: "none", marginTop: "10px", padding: "6px 18px", cursor: "pointer" });
  reload.onclick = (): void => reloadOnFreshBuild();

  panel.append(logo, message, reload);
  overlay.append(panel);
  document.body.append(overlay);

  return {
    offerManualReload: (): void => {
      message.textContent = "The new version could not be reached. Please reload to try again.";
      reload.style.display = "inline-block";
    },
  };
}

function reloadOnFreshBuild(): void {
  // location.reload() is allowed to reuse the cached document, and sites deployed before the
  // Web.config cache headers landed serve index.html with no Cache-Control at all, so change the
  // URL to guarantee a fresh request. The route lives in the hash and is preserved.
  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
  window.location.replace(url.toString());
}
